import { computed, makeObservable } from 'mobx'

import { View } from 'modules/shift_management/lib/types'
import ViewOptions, {
  CoverageOptions,
  EmployeeOptions,
  OtherOptions,
  ScheduleOptions,
} from 'modules/shift_management/store/internal/models/ViewOptions'

interface Params {
  canUseViewOptions: boolean
}

class AllViewOptions {
  allViewOptions: ViewOptions[]

  constructor(viewOptions: ViewOptions[]) {
    this.allViewOptions = viewOptions.length
      ? viewOptions
      : [
          new ViewOptions({ viewType: 'daily' }),
          new ViewOptions({ viewType: 'weekly' }),
          new ViewOptions({ viewType: 'monthly' }),
          new ViewOptions({ viewType: 'four-weeks' }),
        ]

    makeObservable(this, {
      _groupByType: computed,
    })
  }

  get _groupByType(): Record<string, ViewOptions> {
    return this.allViewOptions.reduce(
      (result: Record<string, ViewOptions>, viewOptions: ViewOptions) => {
        result[viewOptions.viewType] = viewOptions
        return result
      },
      {}
    )
  }

  addViewOptions(view: View): ViewOptions {
    const newViewOptions = ViewOptions.defaultForView(view)
    this.allViewOptions.push(newViewOptions)
    return newViewOptions
  }

  viewOptionsFor(view: View, params: Params = { canUseViewOptions: false }): ViewOptions {
    if (!params.canUseViewOptions) return ViewOptions.defaultForView(view)

    const viewOption = this._groupByType[view.viewType]

    if (viewOption) return viewOption

    return this.addViewOptions(view)
  }

  toggleEmployeeOption(view: View, option: keyof EmployeeOptions): AllViewOptions {
    const viewOptions = this.viewOptionsFor(view, { canUseViewOptions: true })
    viewOptions.toggleEmployeeOption(option)
    return new AllViewOptions([...this.allViewOptions])
  }

  toggleScheduleOption(view: View, option: keyof ScheduleOptions): AllViewOptions {
    const viewOptions = this.viewOptionsFor(view, { canUseViewOptions: true })
    viewOptions.toggleScheduleOption(option)
    return new AllViewOptions([...this.allViewOptions])
  }

  toggleCoverageOption(view: View, option: keyof CoverageOptions): AllViewOptions {
    const viewOptions = this.viewOptionsFor(view, { canUseViewOptions: true })
    viewOptions.toggleCoverageOption(option)
    return new AllViewOptions([...this.allViewOptions])
  }

  toggleOtherOption(view: View, option: keyof OtherOptions): AllViewOptions {
    const viewOptions = this.viewOptionsFor(view, { canUseViewOptions: true })
    viewOptions.toggleOtherOption(option)
    return new AllViewOptions([...this.allViewOptions])
  }

  resetAllOptions(view: View): AllViewOptions {
    const vo = this.viewOptionsFor(view, { canUseViewOptions: true })
    vo.resetAllOptions()
    return new AllViewOptions([...this.allViewOptions])
  }
}

export default AllViewOptions
